import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useAddNotification} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import {debounce, getErrorMessage} from '@yandex-delivery/utils'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useFormContext} from 'react-hook-form'

import {useConfigContext} from '../../../contexts'
import {EventGoal, useFormUserEventHandler} from '../../../lib/metrika'
import {store} from '../../../store'
import type {OrderFormState} from '../../../types'
import {getAcceptOfferSuccessHandler, toFormRequestBody} from '../lib'
import {setLayoutToStorage} from './LocaleStorage'
import {useAcceptOffer} from './use-accept-offer'
import {useDefaultValuesAndDraftSync} from './use-default-values-and-draft-sync'
import {useGetDeliveryFormData} from './use-get-delivery-form-data'

export function useOrderForm() {
  const config = useConfigContext()
  const [isSharingLinkLoading, setIsSharingLinkLoading] = useState<boolean>(false)
  const {watch, setValue} = useFormContext()
  const {clearDraftValues, updateDraftValues} = useDefaultValuesAndDraftSync(config)
  const getDeliveryFormData = useGetDeliveryFormData(clearDraftValues)
  const addNotification = useAddNotification()
  const i18n = useI18n()
  const queryClient = useQueryClient()
  const handleReachGoal = useFormUserEventHandler()
  const [
    submode,
    isBusiness,
    whatsInside,
    packageSize,
    nddSourcePoint,
    nddSourceCity,
    nddSourcePhone,
    nddDestinationPoint,
    nddDestinationCity,
    nddDestinationPhone,
  ] = watch([
    'selected_submode',
    'is_business',
    'whats_inside',
    'package_size',
    'ndd.route.source.pickupPoint',
    'ndd.route.source.city',
    'ndd.route.source.phone',
    'ndd.route.destination.pickupPoint',
    'ndd.route.destination.city',
    'ndd.route.destination.phone',
  ])
  const successSubmitGoal = () => {
    handleReachGoal(
      submode === 'cargo_intercity' ? EventGoal.SuccessOrderCargoIntercityCreate : EventGoal.SuccessOrderCreate,
    )
  }

  const {
    data: layout,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['orderForm'],
    queryFn: getDeliveryFormData,
    retry: 4,
    // 1 сек, 2 сек, 4сек, 8сек
    retryDelay: attemptIndex => 1000 * Math.pow(2, attemptIndex),
  })

  const showError = useCallback(
    (error?: Error) => {
      addNotification(error ? getErrorMessage(error) : i18n('request.mutation.error', 'Ошибка запроса'), {
        type: 'error',
      })
    },
    [addNotification, i18n],
  )
  const updateFormState = useMemo(() => {
    const debounceMs = 500
    return debounce(async () => {
      await queryClient.invalidateQueries({queryKey: ['orderForm']})
    }, debounceMs)
  }, [queryClient])

  const saveLayoutToStorageDebounced = useMemo(
    () =>
      debounce(layout => {
        setLayoutToStorage(layout)
      }, 1000),
    [],
  )

  const handleChange = useCallback(
    (formState: OrderFormState) => {
      updateDraftValues(formState)
      if (layout) {
        saveLayoutToStorageDebounced(layout)
      }

      // Валидируем телефон прежде чем инициировать запросы
      const phoneCandidates: Array<string | undefined> = [
        formState?.ndd?.route?.source?.phone as string | undefined,
        formState?.ndd?.route?.destination?.phone as string | undefined,
      ].filter(Boolean)

      const hasInvalidPhone = phoneCandidates.some(phone => 
        !(config.checkIsValidPhone?.(phone) ?? true)
      )      
      if (hasInvalidPhone) {
        // Не дергаем перезапросы, пока телефон не пройдет валидацию
        return
      }

      return updateFormState()
    },
    [updateDraftValues, updateFormState, layout, saveLayoutToStorageDebounced, config],
  )

  const handleAcceptOfferSuccess = useMemo(
    () =>
      getAcceptOfferSuccessHandler({
        clearDraftValues,
        onLoadingStatusChange: setIsSharingLinkLoading,
        onReachGoal: successSubmitGoal,
        showError,
        sharingProvider: store.state.sharingProvider,
        config,
      }),
    [clearDraftValues, config, handleReachGoal, showError],
  )

  const {isPending: isAcceptOfferPending, mutate: mutateAcceptOffer} = useAcceptOffer(config, {
    onError: async error => {
      showError(error)
      const errorCode = error.body?.code
      if (errorCode === 'forbidden_by_safe_flow_challenges') {
        await updateFormState()
      }
    },
    onSuccess: handleAcceptOfferSuccess,
  })

  const handleSubmit = useCallback(
    (data: OrderFormState) => {
      if (!store.state.offerId) {
        showError()
        return
      }

      const state = toFormRequestBody(data, config)

      mutateAcceptOffer({
        mode: 'b2b_external_order',
        state,
        offers: [{id: store.state.offerId}],
      })
    },
    [config, mutateAcceptOffer, showError],
  )
  const isOverlayVisible = isAcceptOfferPending || isSharingLinkLoading

  useEffect(() => {
    return watch(handleChange).unsubscribe
  }, [handleChange, watch])

  useEffect(() => {
    /** бизнес-логика: сношу поле оффера НДД на любой чих формы */
    setValue('ndd_offer_name', undefined)
  }, [
    submode,
    isBusiness,
    whatsInside,
    packageSize,
    nddSourcePoint,
    nddSourceCity,
    nddSourcePhone,
    nddDestinationPoint,
    nddDestinationCity,
    nddDestinationPhone,
    setValue,
  ])

  return {
    layout: Array.isArray(layout) ? layout?.[0]?.override?.layout : undefined,
    isLoading,
    isFetching,
    isOverlayVisible,
    onChange: handleChange,
    onSubmit: handleSubmit,
  }
}
